import React from 'react';
import { AlertNotification, Link, Typography } from '@commonsku/styles';
import ConfirmPopup from './ConfirmPopup';
import CREDIT_CARD_PROVIDERS, { PAYMENTS_DASHBOARD_URL } from '../constants/CreditCardProviders';

type RefundableCheckout = {
    order_id: string;
    payment_id?: string | null;
    payment_provider?: string | null;
};

export interface RefundCheckoutsPopupProps {
    selectedCheckouts: RefundableCheckout[];
    hasPurchaseOrders: boolean;
    /** Refund-and-delete the checkout (Shops checkout list) instead of refund-only (order page). */
    isDeleteFlow?: boolean;
    confirmLoading?: boolean;
    errorMessage?: string | null;
    onClose: () => void;
    onConfirm: () => void;
}

const RefundCheckoutsPopup = ({
    selectedCheckouts,
    hasPurchaseOrders,
    isDeleteFlow = false,
    confirmLoading = false,
    errorMessage = null,
    onClose,
    onConfirm,
}: RefundCheckoutsPopupProps) => {
    const hasCommonskuPayment = selectedCheckouts.some(
        c => c.payment_provider === CREDIT_CARD_PROVIDERS.COMMONSKU_PAYMENTS.value && !!c.payment_id
    );
    const otherProviderValues = Array.from(new Set(
        selectedCheckouts
            .map(c => c.payment_provider)
            .filter((provider): provider is string =>
                !!provider && provider !== CREDIT_CARD_PROVIDERS.COMMONSKU_PAYMENTS.value
            )
    ));
    const hasManualProvider = otherProviderValues.length > 0;
    const otherProviderLabels = otherProviderValues
        .map(value => Object.values(CREDIT_CARD_PROVIDERS).find(provider => provider.value === value)?.label ?? value)
        .join(' and ');

    const showOrderUnchangedWarning = !hasPurchaseOrders;
    const showFeeWarning = isDeleteFlow ? hasCommonskuPayment : true;
    const showLearnMore = isDeleteFlow && !hasCommonskuPayment;

    const title = !isDeleteFlow
        ? 'Refund checkout'
        : hasCommonskuPayment ? 'Delete & Refund checkout' : 'Delete checkout';
    const confirmLabel = !isDeleteFlow
        ? 'Refund checkout'
        : hasCommonskuPayment ? 'Delete & Refund checkouts' : 'Delete checkouts';

    const mixedProviderRefundText = (
        <>
            {'Checkout(s) paid via '}
            <Typography.Span bold>{CREDIT_CARD_PROVIDERS.COMMONSKU_PAYMENTS.label}</Typography.Span>
            {' will be automatically refunded to the client(s). Refunds may take 3-5 business days to process.'}
        </>
    );

    let bodyText: React.ReactNode;
    if (isDeleteFlow) {
        if (hasCommonskuPayment && hasManualProvider) {
            bodyText = (
                <>
                    {'Selected checkout(s) will be deleted from the shop.'}
                    <br /><br />
                    {mixedProviderRefundText}
                </>
            );
        } else if (hasCommonskuPayment) {
            bodyText = 'Selected checkout(s) will be deleted from the shop and refunded to the client(s). Refunds may take 3-5 business days to process.';
        } else {
            bodyText = 'Selected checkout(s) will be deleted from the shop.';
        }
    } else {
        bodyText = hasManualProvider
            ? mixedProviderRefundText
            : 'Selected checkout(s) will be refunded to the client(s). Refunds may take 3-5 business days to process.';
    }

    return (
        <ConfirmPopup
            title={title}
            showCloseIcon
            cancelLabel="Cancel"
            confirmLabel={confirmLabel}
            confirmVariant="error"
            confirmLoading={confirmLoading}
            style={{ width: 400 }}
            onClose={onClose}
            onConfirm={onConfirm}
        >
            {errorMessage && (
                <AlertNotification alertType="error">
                    <Typography.Text fontSize={16} lineHeight="24px" noBottom>{errorMessage}</Typography.Text>
                </AlertNotification>
            )}
            <Typography.Text fontSize={16} lineHeight="24px" noBottom>
                {bodyText}
            </Typography.Text>
            {hasManualProvider && (
                <AlertNotification alertType="warn">
                    <Typography.Text fontSize={16} lineHeight="24px" noBottom>
                        {'Checkout(s) paid via '}
                        <Typography.Span bold>{otherProviderLabels}</Typography.Span>
                        {' must be refunded directly in those platforms.'}
                    </Typography.Text>
                </AlertNotification>
            )}
            {showOrderUnchangedWarning && (
                <AlertNotification alertType="warn">
                    <Typography.Text fontSize={16} lineHeight="24px" noBottom>
                        {"Refunding the selected checkout(s) won't update the order. Decide whether to keep or remove associated products from the order."}
                    </Typography.Text>
                </AlertNotification>
            )}
            {showFeeWarning && (
                <AlertNotification alertType="warn">
                    <Typography.Text fontSize={16} lineHeight="24px" noBottom>
                        {'Credit card processing fees from '}
                        <Typography.Span bold>{CREDIT_CARD_PROVIDERS.COMMONSKU_PAYMENTS.label}</Typography.Span>
                        {" won't be refunded."}
                    </Typography.Text>
                </AlertNotification>
            )}
            {showLearnMore && (
                <AlertNotification alertType="neutral">
                    <div>
                        <Typography.Text fontSize={16} lineHeight="24px" noBottom>
                            {'With '}
                            <Typography.Span bold>{CREDIT_CARD_PROVIDERS.COMMONSKU_PAYMENTS.label}</Typography.Span>
                            {' you can issue refunds directly from commonsku.'}
                        </Typography.Text>
                        <Link as="a" href={PAYMENTS_DASHBOARD_URL} target="_blank" style={{ display: 'block' }}>
                            Learn more
                        </Link>
                    </div>
                </AlertNotification>
            )}
        </ConfirmPopup>
    );
};

export default RefundCheckoutsPopup;
